import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/ui/password-input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { LockKey } from "@phosphor-icons/react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Reset link is missing a token. Request a new email.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success(`Password updated across ${data.workspaces} workspace(s). Sign in with your new password.`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md" data-testid="reset-password-page">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-primary/10 p-2"><LockKey size={20} className="text-primary" /></div>
            <CardTitle>Choose a new password</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="space-y-4">
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                Your password has been reset. Sign in to continue.
              </div>
              <Button onClick={() => nav("/login")} className="w-full">Sign in</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>New password</Label>
                <PasswordInput
                  autoFocus required
                  value={pw}
                  onChange={e => setPw(e.target.value)}
                  data-testid="reset-pw"
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Confirm password</Label>
                <PasswordInput
                  required
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  data-testid="reset-confirm"
                  autoComplete="new-password"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Applies to every workspace this email owns. Minimum 8 characters.
              </p>
              <Button type="submit" className="w-full" disabled={busy || !token} data-testid="reset-submit">
                {busy ? "Updating…" : "Update password"}
              </Button>
              <Link to="/login" className="block text-center text-xs text-primary hover:underline">Cancel</Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
